import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  UserIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  EnvelopeIcon,
  PlusIcon,
  XMarkIcon,
  TrashIcon,
  BriefcaseIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import {
  OnboardingRequestsClient,
  TemplateTasksClient,
  RequestItemsClient,
  LocationsClient,
  DepartmentsClient,
  EmployeesClient,
  OnboardingRequestPreviewDto,
  TemplateTaskListDto,
  RequestItemPreviewDto,
  RequestItemCategoryDto,
  UpdateRequestItemPicRequest,
  AddCustomRequestItemCommand,
  UpdateEmployeeCommand,
  LocationDto,
  DepartmentDto,
  TeamDto,
  RequestStatus
} from '../../web-api-client';
import { toast } from 'sonner';
import { API_CONFIG } from '../../config/api';
import { usersService, User } from '../../services/usersService';
import { teamsService } from '../../services/teamsService';
import { createAuthenticatedFetch } from '../../utils/apiClient';
import NewOffboardingRequestForm from '../offboarding/NewOffboardingRequestForm';
import AddTaskModal, { AddTaskData } from '../../components/checklist/AddTaskModal';
import { getErrorMessage } from '../../utils/errorHelpers';

export default function EditOnboardingRequest() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState<OnboardingRequestPreviewDto | null>(null);
  const [locations, setLocations] = useState<LocationDto[]>([]);
  const [departments, setDepartments] = useState<DepartmentDto[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [teams, setTeams] = useState<TeamDto[]>([]);
  const [availableTemplateTasks, setAvailableTemplateTasks] = useState<TemplateTaskListDto[]>([]);
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [approverEmailErrors, setApproverEmailErrors] = useState<{ [key: number]: string }>({});
  const [tempIdCounter, setTempIdCounter] = useState<number>(1);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [declining, setDeclining] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [discarding, setDiscarding] = useState(false);

  // Offboarding wizard state (for Approved status decline)
  const [showOffboardingWizard, setShowOffboardingWizard] = useState(false);
  const [offboardingEmployeeId, setOffboardingEmployeeId] = useState<number | undefined>();
  const [offboardingRemarks, setOffboardingRemarks] = useState<string | undefined>();

  const [formData, setFormData] = useState({
    employeeName: '',
    locationId: 0,
    departmentId: 0,
    designation: '',
    personalEmail: '',
    immediateSuperiorEmail: '',
    reportingSuperiorEmail: '',
    employmentDate: ''
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch request details
      const onboardingClient = new OnboardingRequestsClient(API_CONFIG.baseUrl, createAuthenticatedFetch());
      const requestData = await onboardingClient.getOnboardingRequestPreview(Number(id));
      setPreview(requestData);

      // Fetch additional data
      const [locationsData, usersData, teamsData, templateTasksData] = await Promise.all([
        new LocationsClient(API_CONFIG.baseUrl, createAuthenticatedFetch()).getLocations(),
        usersService.getUsers(),
        teamsService.getTeams(),
        new TemplateTasksClient(API_CONFIG.baseUrl, createAuthenticatedFetch()).getTemplateTasks(undefined, undefined, true)
      ]);

      setLocations(locationsData);
      setUsers(usersData);
      setTeams(teamsData);
      setAvailableTemplateTasks(templateTasksData);

      // Fetch departments for the selected location
      if (requestData.employee?.locationId) {
        const departmentsData = await new DepartmentsClient(API_CONFIG.baseUrl, createAuthenticatedFetch())
          .getDepartments(requestData.employee.locationId);
        setDepartments(departmentsData);
      }

      // Populate form data from request AFTER all data is loaded
      if (requestData.employee) {
        setFormData({
          employeeName: requestData.employee.fullName || '',
          locationId: requestData.employee.locationId || 0,
          departmentId: requestData.employee.departmentId || 0,
          designation: requestData.employee.designation || '',
          personalEmail: requestData.employee.personalEmail || '',
          immediateSuperiorEmail: requestData.employee.immediateSuperiorEmail || '',
          reportingSuperiorEmail: requestData.employee.reportingSuperiorEmail || '',
          employmentDate: requestData.employee.employmentDate
            ? new Date(requestData.employee.employmentDate).toISOString().split('T')[0]
            : ''
        });
      }

    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load request details');
    } finally {
      setLoading(false);
    }
  };

  const handleLocationChange = async (locationId: number) => {
    setFormData(prev => ({ ...prev, locationId, departmentId: 0 }));

    if (locationId > 0) {
      try {
        const departmentsData = await new DepartmentsClient(API_CONFIG.baseUrl, createAuthenticatedFetch())
          .getDepartments(locationId);
        setDepartments(departmentsData);
      } catch (error) {
        console.error('Failed to fetch departments:', error);
        toast.error('Failed to load departments');
      }
    } else {
      setDepartments([]);
    }
  };

  const handleAddTaskFromModal = async (taskData: AddTaskData): Promise<void> => {
    if (!preview) return;

    const tempId = -(Date.now() + tempIdCounter);
    setTempIdCounter(prev => prev + 1);

    const newItem = new RequestItemPreviewDto({
      id: tempId,
      itemName: taskData.taskName,
      description: taskData.description,
      requiresApproval: false,
      templateTaskId: taskData.templateTaskId
    });

    if (!taskData.categoryId) {
      toast.error('Cannot add task without a category');
      return;
    }

    const existingCategory = preview.categories?.find(
      cat => cat.categoryId === taskData.categoryId
    );

    if (existingCategory) {
      const updatedCategories = preview.categories?.map(cat => {
        if (cat.categoryId === taskData.categoryId) {
          return new RequestItemCategoryDto({
            ...cat,
            items: [...(cat.items || []), newItem]
          });
        }
        return cat;
      });

      setPreview(new OnboardingRequestPreviewDto({
        ...preview,
        categories: updatedCategories
      }));
    } else {
      const newCategory = new RequestItemCategoryDto({
        categoryId: taskData.categoryId,
        categoryName: taskData.categoryName,
        items: [newItem]
      });

      setPreview(new OnboardingRequestPreviewDto({
        ...preview,
        categories: [...(preview.categories || []), newCategory]
      }));
    }
  };

  const handleRemoveTask = (categoryId: number | undefined, taskId: number) => {
    if (!preview) return;

    const updatedCategories = preview.categories?.map(cat => {
      if (cat.categoryId === categoryId) {
        return new RequestItemCategoryDto({
          ...cat,
          items: cat.items?.filter(item => item.id !== taskId)
        });
      }
      return cat;
    });

    setPreview(new OnboardingRequestPreviewDto({
      ...preview,
      categories: updatedCategories
    }));

    toast.success('Task removed');
  };

  const handleUpdatePIC = (taskId: number, categoryId: number | undefined, newPicId: string) => {
    if (!preview) return;

    let actualPicId: string | undefined = undefined;
    let actualTeamId: number | undefined = undefined;
    let assignedPersonName: string | undefined = undefined;
    let assignedTeamName: string | undefined = undefined;

    if (newPicId.startsWith('team-')) {
      const teamIdStr = newPicId.replace('team-', '');
      actualTeamId = parseInt(teamIdStr);
      const selectedTeam = teams.find(t => t.id === actualTeamId);
      assignedTeamName = selectedTeam?.name;
    } else if (newPicId.startsWith('person-')) {
      actualPicId = newPicId.replace('person-', '');
      const selectedUser = users.find(u => u.id === actualPicId);
      assignedPersonName = selectedUser?.userName;
    } else if (newPicId) {
      actualPicId = newPicId;
      const selectedUser = users.find(u => u.id === actualPicId);
      assignedPersonName = selectedUser?.userName;
    }

    if (categoryId) {
      const updatedCategories = preview.categories?.map(cat => {
        if (cat.categoryId === categoryId) {
          return new RequestItemCategoryDto({
            ...cat,
            items: cat.items?.map(item => {
              if (item.id === taskId) {
                return new RequestItemPreviewDto({
                  ...item,
                  picId: actualPicId,
                  teamId: actualTeamId,
                  assignedPersonName: assignedPersonName,
                  assignedTeamName: assignedTeamName
                });
              }
              return item;
            })
          });
        }
        return cat;
      });

      setPreview(new OnboardingRequestPreviewDto({
        ...preview,
        categories: updatedCategories
      }));
      toast.success('Assignment updated');
    }
  };

  const handleUpdateItemApproval = (taskId: number, categoryId: number | undefined, requiresApproval: boolean, approverEmail?: string) => {
    if (!preview) return;

    if (requiresApproval && approverEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(approverEmail)) {
        setApproverEmailErrors(prev => ({
          ...prev,
          [taskId]: 'Please enter a valid email address'
        }));
      } else {
        setApproverEmailErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[taskId];
          return newErrors;
        });
      }
    } else if (!requiresApproval) {
      setApproverEmailErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[taskId];
        return newErrors;
      });
    } else if (requiresApproval && !approverEmail) {
      setApproverEmailErrors(prev => ({
        ...prev,
        [taskId]: 'Approver email is required'
      }));
    }

    if (categoryId) {
      const updatedCategories = preview.categories?.map(cat => {
        if (cat.categoryId === categoryId) {
          return new RequestItemCategoryDto({
            ...cat,
            items: cat.items?.map(item => {
              if (item.id === taskId) {
                return new RequestItemPreviewDto({
                  ...item,
                  requiresApproval: requiresApproval,
                  approverEmail: requiresApproval ? (approverEmail || '') : undefined
                });
              }
              return item;
            })
          });
        }
        return cat;
      });

      setPreview(new OnboardingRequestPreviewDto({
        ...preview,
        categories: updatedCategories
      }));
    }
  };

  const validateForm = (): boolean => {
    // Validate employee information
    if (!formData.employeeName.trim()) {
      toast.error('Employee name is required');
      return false;
    }
    if (!formData.locationId || formData.locationId === 0) {
      toast.error('Please select a location');
      return false;
    }
    if (!formData.departmentId || formData.departmentId === 0) {
      toast.error('Please select a department');
      return false;
    }
    if (!formData.designation.trim()) {
      toast.error('Designation is required');
      return false;
    }
    if (!formData.personalEmail.trim()) {
      toast.error('Personal email is required');
      return false;
    }
    if (!formData.employmentDate) {
      toast.error('Employment date is required');
      return false;
    }

    // Validate email formats
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.personalEmail)) {
      toast.error('Please enter a valid personal email');
      return false;
    }
    if (formData.immediateSuperiorEmail && !emailRegex.test(formData.immediateSuperiorEmail)) {
      toast.error('Please enter a valid immediate superior email');
      return false;
    }
    if (formData.reportingSuperiorEmail && !emailRegex.test(formData.reportingSuperiorEmail)) {
      toast.error('Please enter a valid second level approver email');
      return false;
    }

    return true;
  };

  const handleSave = async (skipNavigation = false) => {
    if (!validateForm()) {
      return false;
    }

    // Validate all items with requires approval have valid emails
    const allItems = preview?.categories?.flatMap(cat => cat.items || []) || [];
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidItems = allItems.filter(item =>
      item.requiresApproval === true && (!item.approverEmail || !item.approverEmail.trim() || !emailRegex.test(item.approverEmail.trim()))
    );

    if (invalidItems.length > 0) {
      // Set error state for each invalid item
      const newErrors: { [key: number]: string } = {};
      invalidItems.forEach(item => {
        if (item.id) {
          newErrors[item.id] = !item.approverEmail || !item.approverEmail.trim()
            ? 'Approver email is required'
            : 'Please enter a valid email address';
        }
      });
      setApproverEmailErrors(prev => ({ ...prev, ...newErrors }));
      toast.error('Please provide valid approver emails for all items marked as "Requires Approval"');
      return false;
    }

    // Also check if there are any existing approver email errors
    if (Object.keys(approverEmailErrors).length > 0) {
      toast.error('Please fix approver email errors before saving');
      return false;
    }

    setSubmitting(true);
    try {
      // Update employee information
      if (preview?.employee?.id) {
        const employeesClient = new EmployeesClient(API_CONFIG.baseUrl, createAuthenticatedFetch());
        await employeesClient.updateEmployee(preview.employee.id, new UpdateEmployeeCommand({
          id: preview.employee.id,
          fullName: formData.employeeName,
          locationId: formData.locationId,
          departmentId: formData.departmentId,
          designation: formData.designation,
          personalEmail: formData.personalEmail,
          immediateSuperiorEmail: formData.immediateSuperiorEmail || undefined,
          reportingSuperiorEmail: formData.reportingSuperiorEmail || undefined,
          employmentDate: new Date(formData.employmentDate)
        }));
      }

      // STEP 1: Fetch actual request FIRST to get current state
      const actualRequest = await new OnboardingRequestsClient(API_CONFIG.baseUrl, createAuthenticatedFetch())
        .getOnboardingRequestPreview(Number(id));

      const itemsClient = new RequestItemsClient(API_CONFIG.baseUrl, createAuthenticatedFetch());

      // STEP 2: Handle deleted items - delete items that exist in actual but not in preview
      if (actualRequest.categories) {
        // Get all preview item IDs for quick lookup
        const previewItemIds = new Set<number>();
        if (preview?.categories) {
          for (const previewCategory of preview.categories) {
            if (previewCategory.items) {
              for (const item of previewCategory.items) {
                // Only track positive IDs (existing items)
                if (item.id && item.id > 0) {
                  previewItemIds.add(item.id);
                }
              }
            }
          }
        }

        // Delete items that exist in actual but not in preview
        for (const actualCategory of actualRequest.categories) {
          if (actualCategory.items) {
            for (const actualItem of actualCategory.items) {
              if (actualItem.id && !previewItemIds.has(actualItem.id)) {
                await itemsClient.removeRequestItem(actualItem.id);
              }
            }
          }
        }
      }

      // STEP 3: Add new items and update existing ones
      if (preview?.categories) {
        for (const category of preview.categories) {
          if (category.items) {
            for (const item of category.items) {
              // Check if this is a new item (negative ID)
              if (item.id && item.id < 0) {
                // Create new custom request item
                await itemsClient.addCustomRequestItem(new AddCustomRequestItemCommand({
                  boardingRequestId: Number(id),
                  itemName: item.itemName,
                  description: item.description,
                  picId: item.picId,
                  teamId: item.teamId,
                  requiresApproval: item.requiresApproval || false,
                  approverEmail: item.approverEmail,
                  templateCategoryId: category.categoryId
                }));
              } else if (item.id && item.id > 0) {
                // Update existing items (positive IDs)
                await itemsClient.updateRequestItemPic(item.id, new UpdateRequestItemPicRequest({
                  picId: item.picId,
                  teamId: item.teamId,
                  requiresApproval: item.requiresApproval,
                  approverEmail: item.approverEmail
                }));
              }
            }
          }
        }
      }

      // Reload the preview to get actual IDs for newly created items
      const updatedPreview = await new OnboardingRequestsClient(API_CONFIG.baseUrl, createAuthenticatedFetch())
        .getOnboardingRequestPreview(Number(id));
      setPreview(updatedPreview);

      if (!skipNavigation) {
        toast.success('Changes saved successfully');
        navigate(`/onboarding/${id}`);
      }
      return true;
    } catch (error: unknown) {
      console.error('Failed to save changes:', error);
      toast.error(getErrorMessage(error, 'Failed to save changes'));
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    // Validate employee information first
    if (!validateForm()) {
      return;
    }

    // Validate all items with requires approval have valid emails
    const allItems = preview?.categories?.flatMap(cat => cat.items || []) || [];
    const invalidItems = allItems.filter(item =>
      item.requiresApproval && (!item.approverEmail?.trim() || approverEmailErrors[item.id!])
    );

    if (invalidItems.length > 0) {
      toast.error('Please provide valid approver emails for all items marked as "Requires Approval"');
      return;
    }

    setSubmitting(true);
    try {
      // Save changes first (skip navigation since we'll navigate after submit)
      const saveSuccess = await handleSave(true);

      if (!saveSuccess) {
        return;
      }

      // Then submit
      const onboardingClient = new OnboardingRequestsClient(API_CONFIG.baseUrl, createAuthenticatedFetch());
      await onboardingClient.submitOnboardingRequest(Number(id));

      toast.success('Onboarding request submitted successfully');
      navigate('/onboarding');
    } catch (error: unknown) {
      console.error('Failed to submit request:', error);
      toast.error(getErrorMessage(error, 'Failed to submit request'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    if (!declineReason.trim()) {
      toast.error('Please provide a reason for declining');
      return;
    }

    setDeclining(true);
    try {
      // Use fetch directly to get the full response
      const response = await fetch(`${API_CONFIG.baseUrl}/api/onboardingrequests/${id}/decline`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ reason: declineReason })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to decline request');
      }

      const result = await response.json();
      setShowDeclineModal(false);

      // Open offboarding wizard with employee pre-selected
      toast.success('Onboarding declined. Please configure the offboarding request.');
      setOffboardingEmployeeId(result.employeeId);
      setOffboardingRemarks(`Declined onboarding request. Reason: ${result.declineReason || declineReason}`);
      setShowOffboardingWizard(true);
    } catch (error: unknown) {
      console.error('Failed to decline request:', error);
      toast.error(getErrorMessage(error, 'Failed to decline request'));
    } finally {
      setDeclining(false);
    }
  };

  // Handle offboarding wizard success
  const handleOffboardingWizardSuccess = () => {
    setShowOffboardingWizard(false);
    toast.success('Offboarding request created successfully');
    navigate('/offboarding');
  };

  const handleDiscard = async () => {
    setDiscarding(true);
    try {
      const onboardingClient = new OnboardingRequestsClient(API_CONFIG.baseUrl, createAuthenticatedFetch());
      await onboardingClient.discardOnboardingRequest(Number(id));

      toast.success('Draft discarded successfully');
      setShowDiscardModal(false);
      navigate('/onboarding');
    } catch (error: unknown) {
      console.error('Failed to discard draft:', error);
      toast.error(getErrorMessage(error, 'Failed to discard draft'));
    } finally {
      setDiscarding(false);
    }
  };

  // Helper to check if request is in Draft status
  const isDraft = preview?.status === RequestStatus.Draft;

  // Check if Decline button should be shown (for pending approval and approved statuses)
  const showDeclineButton = preview?.status === RequestStatus.PendingFirstApproval ||
    preview?.status === RequestStatus.PendingSecondApproval ||
    preview?.status === RequestStatus.Approved;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  if (!preview) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Request not found</p>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/onboarding')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Onboarding Requests
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Edit Onboarding Request</h1>
        <p className="text-sm text-gray-600 mt-1">Make changes to employee information and tasks</p>
      </div>

      {/* Employee Information */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <UserIcon className="h-5 w-5 mr-2 text-gray-600" />
          Employee Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
              <UserIcon className="h-4 w-4 mr-1" />
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.employeeName}
              onChange={(e) => setFormData({ ...formData, employeeName: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
              <BuildingOfficeIcon className="h-4 w-4 mr-1" />
              Location <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.locationId || ''}
              onChange={(e) => handleLocationChange(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              required
            >
              <option value="">Select Location</option>
              {locations.map(location => (
                <option key={location.id} value={location.id}>{location.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
              <BuildingOfficeIcon className="h-4 w-4 mr-1" />
              Department <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.departmentId || ''}
              onChange={(e) => setFormData({ ...formData, departmentId: Number(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              required
              disabled={!formData.locationId || departments.length === 0}
            >
              <option value="">Select Department</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.id}>{dept.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
            <BriefcaseIcon className="h-4 w-4 mr-1" />
              Designation <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.designation}
              onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
              <CalendarIcon className="h-4 w-4 mr-1" />
              Employment Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.employmentDate}
              onChange={(e) => setFormData({ ...formData, employmentDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
              <EnvelopeIcon className="h-4 w-4 mr-1" />
              Personal Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={formData.personalEmail}
              onChange={(e) => setFormData({ ...formData, personalEmail: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
              <EnvelopeIcon className="h-4 w-4 mr-1" />
              Immediate Superior Email <span className="text-red-500 ml-1">*</span>
              <span className="relative group ml-1">
                <InformationCircleIcon className="h-4 w-4 text-gray-400" />
                <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                  First Level Approver (Required)
                </span>
              </span>
            </label>
            <input
              type="email"
              required
              value={formData.immediateSuperiorEmail}
              onChange={(e) => setFormData({ ...formData, immediateSuperiorEmail: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
              <EnvelopeIcon className="h-4 w-4 mr-1" />
              Second Level Approver Email
              <span className="relative group ml-1">
                <InformationCircleIcon className="h-4 w-4 text-gray-400" />
                <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                  Second Level Approver (Optional)
                </span>
              </span>
            </label>
            <input
              type="email"
              value={formData.reportingSuperiorEmail}
              onChange={(e) => setFormData({ ...formData, reportingSuperiorEmail: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>
      </div>

      {/* Tasks Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Onboarding Tasks</h2>
          <button
            type="button"
            onClick={() => setShowAddTaskModal(true)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Task
          </button>
        </div>

        {/* Categories and Tasks */}
        {preview.categories && preview.categories.length > 0 ? (
          <div className="space-y-4">
            {preview.categories
              .filter(category => category.items && category.items.length > 0)
              .map((category) => (
              <div key={category.categoryId} className="border border-gray-200 rounded-lg">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                  <h5 className="font-medium text-gray-900">{category.categoryName}</h5>
                </div>
                <div className="divide-y divide-gray-100">
                  {category.items && category.items.map((item, idx) => (
                    <div key={idx} className="px-4 py-3 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{item.itemName}</p>
                          {item.description && (
                            <p className="text-xs text-gray-600 mt-1">{item.description}</p>
                          )}

                          {/* PIC Selection */}
                          <div className="flex items-center mt-2 space-x-2">
                            <span className="text-xs text-gray-600 font-medium">Assignee:</span>
                            <select
                              value={
                                item.teamId ? `team-${item.teamId}` :
                                item.picId ? `person-${item.picId}` :
                                ''
                              }
                              onChange={(e) => handleUpdatePIC(item.id!, category.categoryId, e.target.value)}
                              className="text-xs border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            >
                              <option value="">Select assignment...</option>
                              <optgroup label="─── Teams ───">
                                {teams.map((team) => (
                                  <option key={`team-${team.id}`} value={`team-${team.id}`}>
                                    👥 {team.name}
                                  </option>
                                ))}
                              </optgroup>
                              <optgroup label="─── Persons ───">
                                {users.map((user) => (
                                  <option key={`person-${user.id}`} value={`person-${user.id}`}>
                                    👤 {user.userName}
                                  </option>
                                ))}
                              </optgroup>
                            </select>
                            {(item.assignedTeamName || item.assignedPersonName) && (
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                item.assignedTeamName ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                              }`}>
                                {item.assignedTeamName ? `👥 ${item.assignedTeamName}` : `👤 ${item.assignedPersonName}`}
                              </span>
                            )}
                          </div>

                          {/* Approval Section */}
                          <div className="mt-3 space-y-2">
                            <label className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={item.requiresApproval || false}
                                onChange={(e) => handleUpdateItemApproval(item.id!, category.categoryId, e.target.checked, item.approverEmail)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <span className="text-xs text-gray-700 font-medium">Requires Approval</span>
                            </label>
                            {item.requiresApproval && (
                              <div>
                                <input
                                  type="email"
                                  value={item.approverEmail || ''}
                                  onChange={(e) => handleUpdateItemApproval(item.id!, category.categoryId, true, e.target.value)}
                                  placeholder="Enter single approver email"
                                  className={`w-full text-xs border rounded px-3 py-2 focus:ring-2 focus:border-transparent ${
                                    approverEmailErrors[item.id!]
                                      ? 'border-red-500 focus:ring-red-500'
                                      : 'border-gray-300 focus:ring-blue-500'
                                  }`}
                                />
                                {approverEmailErrors[item.id!] ? (
                                  <p className="text-xs text-red-600 mt-1">{approverEmailErrors[item.id!]}</p>
                                ) : (
                                  <p className="text-xs text-gray-500 mt-1">Only one email address allowed</p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveTask(category.categoryId, item.id!)}
                          className="ml-4 p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                          title="Remove task"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 text-center py-8">No tasks yet. Click "Add Task" to get started.</p>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between mt-6">
        {/* Left Side - Discard (for Draft) or Decline (for Pending/Approved) */}
        <div>
          {isDraft && (
            <button
              type="button"
              onClick={() => setShowDiscardModal(true)}
              disabled={submitting || discarding}
              className="px-6 py-2 text-sm font-medium text-white bg-gray-600 rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
            >
              <TrashIcon className="h-5 w-5 mr-2" />
              Discard Draft
            </button>
          )}
          {showDeclineButton && (
            <button
              type="button"
              onClick={() => setShowDeclineModal(true)}
              disabled={submitting || declining}
              className="px-6 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
            >
              <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
              Decline
            </button>
          )}
        </div>

        {/* Other Buttons - Right Side */}
        <div className="flex space-x-3">
          <button
            type="button"
            onClick={() => navigate('/onboarding')}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => handleSave(false)}
            disabled={submitting}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save Draft
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="px-6 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
          >
            {submitting ? 'Submitting...' : (
              <>
                Submit for Approval
                <CheckCircleIcon className="ml-2 h-5 w-5" />
              </>
            )}
          </button>
        </div>
      </div>

      {/* Decline Confirmation Modal */}
      {showDeclineModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">Decline Onboarding</h3>
                  <p className="text-sm text-gray-500">This action will reject the onboarding request</p>
                </div>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-700 mb-3">
                  Are you sure you want to decline this onboarding request for <strong>{preview?.employee?.fullName}</strong>?
                  This will mark the onboarding as rejected and initiate the offboarding process.
                </p>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for Declining <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)}
                  placeholder="e.g., Employee rejected the offer, Failed background check..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowDeclineModal(false);
                    setDeclineReason('');
                  }}
                  disabled={declining}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDecline}
                  disabled={declining || !declineReason.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
                >
                  {declining ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Declining...
                    </>
                  ) : (
                    'Confirm Decline'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Discard Draft Confirmation Modal */}
      {showDiscardModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                  <TrashIcon className="h-6 w-6 text-gray-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">Discard Draft</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone</p>
                </div>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-700">
                  Are you sure you want to discard this draft onboarding request for <strong>{preview?.employee?.fullName}</strong>?
                  This will permanently delete the draft and all associated tasks.
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowDiscardModal(false)}
                  disabled={discarding}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDiscard}
                  disabled={discarding}
                  className="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
                >
                  {discarding ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Discarding...
                    </>
                  ) : (
                    'Confirm Discard'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Offboarding Wizard (for Approved status decline) */}
      <NewOffboardingRequestForm
        show={showOffboardingWizard}
        onClose={() => {
          setShowOffboardingWizard(false);
          navigate('/onboarding');
        }}
        onSuccess={handleOffboardingWizardSuccess}
        initialEmployeeId={offboardingEmployeeId}
        initialRemarks={offboardingRemarks}
      />

      {/* Add Task Modal */}
      <AddTaskModal
        isOpen={showAddTaskModal}
        onClose={() => setShowAddTaskModal(false)}
        onSave={handleAddTaskFromModal}
        title="Add Onboarding Task"
      />
    </div>
  );
}
